import React, { useState, useCallback } from "react";
import { FaThumbtack } from "react-icons/fa";
import { useDashboards } from "@/api/dashboard";
import { useSidebarContext } from "@/hooks/use-sidebar";
import DashboardItem, { DropMode } from "./dashboard-item";

const PinnedSection: React.FC = () => {
  const { data: dashboards = [] } = useDashboards();
  const { getPinnedDashboards, pinItem, reorderItems, getItemConfig } =
    useSidebarContext();

  // Drag & drop state
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const pinnedDashboards = getPinnedDashboards(dashboards);

  // Drag & drop handlers
  const handleDragStart = useCallback(
    (e: React.DragEvent, dashboardId: string) => {
      setDraggedId(dashboardId);
    },
    []
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, dashboardId: string, _mode: DropMode) => {
      e.preventDefault();
      if (draggedId && draggedId !== dashboardId) {
        setDragOverId(dashboardId);
      }
    },
    [draggedId]
  );

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent, targetId: string, _mode: DropMode) => {
      e.preventDefault();

      if (!draggedId || draggedId === targetId) {
        setDraggedId(null);
        setDragOverId(null);
        return;
      }

      // Pinned section only supports reordering, not grouping
      // Reorder the pinned dashboards
      const currentOrder = pinnedDashboards.map((d) => d.id!);
      const draggedIndex = currentOrder.indexOf(draggedId);
      const targetIndex = currentOrder.indexOf(targetId);

      if (draggedIndex === -1 || targetIndex === -1) {
        setDraggedId(null);
        setDragOverId(null);
        return;
      }

      // Remove from old position and insert at new position
      const newOrder = [...currentOrder];
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedId);

      // Update the order in the sidebar
      await reorderItems(newOrder.map((id) => ({ id, type: "dashboard" as const })));

      setDraggedId(null);
      setDragOverId(null);
    },
    [draggedId, pinnedDashboards, reorderItems]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  if (pinnedDashboards.length === 0) {
    return null;
  }

  return (
    <div className="px-3 py-2">
      {/* Section Title */}
      <div className="flex items-center gap-2 px-2 mb-1 text-[var(--arc-accent)]">
        <FaThumbtack size={10} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">
          Pinned
        </span>
        <div className="flex-1 h-px bg-[var(--arc-sidebar-border-accent)]" />
      </div>

      {/* Pinned Dashboards with Drag & Drop */}
      <div className="space-y-0.5" onDragEnd={handleDragEnd}>
        {pinnedDashboards.map((dashboard) => (
          <DashboardItem
            key={dashboard.id}
            dashboard={dashboard}
            isPinned={true}
            onUnpin={() => pinItem(dashboard.id!, "dashboard", false)}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            isDragging={draggedId === dashboard.id}
            isDragOver={dragOverId === dashboard.id}
          />
        ))}
      </div>
    </div>
  );
};

PinnedSection.displayName = "PinnedSection";

export default PinnedSection;
